<template>
  <div class="widget w-earnings-analyzer-widget" :class="['font-size-' + fontSize, { bold: isFontBold }]">
    <c-widget-header hideLinkModeSend printView :tradeButtonsSymbol="ric" :wid="wid">
      <c-incremental-search
        slot="left"
        :ric="ric"
        @error="onSymbolSearchError"
        @change-ric="onUpdateSymbol"
        :instrumentTypes="['STO', 'CER']"
      />
      <slot name="buttons" slot="buttons" />
    </c-widget-header>

    <c-widget-control-bar v-if="ric">
      <c-widget-control-bar-group inline>
        <v-form-group :label="$t('white-label.globalLabels.chart.type.label')" label-cols="auto">
          <v-form-select
            :aria-label="$t('white-label.globalLabels.chart.type.label')"
            :value="controlChartType"
            :options="chartTypeOptions"
            @input="onUpdateChartType"
          />
        </v-form-group>
        <v-form-group :label="i18n('controlbar.frequency.label')" label-cols="auto">
          <v-form-select
            :aria-label="i18n('controlbar.frequency.label')"
            :value="controlFrequency"
            :options="frequencyOptions"
            @input="onUpdateFrequency"
          />
        </v-form-group>
        <v-form-group :label="$t('white-label.globalLabels.chart.period.label')" label-cols="auto">
          <v-form-radio-group
            inline
            buttons
            button-variant="primary"
            :checked="controlPeriod"
            :options="periodOptions"
            @input="onUpdatePeriod"
          />
        </v-form-group>
      </c-widget-control-bar-group>
      <v-form-group>
        <v-form-checkbox v-model="controlShowFutureEstimation">{{ i18n('controlbar.future') }}</v-form-checkbox>
      </v-form-group>
    </c-widget-control-bar>

    <c-streaming-error :show="hasStreamingError" />

    <div v-if="ric" class="widget-body" :style="{ 'flex-flow': isNoData ? 'column' : 'row' }">
      <!-- svg pattern container for chart legend symbols; make it invisible by css dimensions, attribute dimensions influence svg children sizing -->
      <svg style="width:0; height:0">
        <defs>
          <pattern id="hiLoRangeSymbolPattern" patternContentUnits="objectBoundingBox" width="1" height="1">
            <g>
              <rect width="1" height="1" />
              <line x1="0" x2="1" y1="-0.3" y2="-0.3" />
              <line x1="0" x2="1" y1="1.3" y2="1.3" />
            </g>
          </pattern>
        </defs>
      </svg>
      <c-loader v-if="isLoading" />
      <v-alert show v-if="isNoData" variant="light" align="center">{{
        $t('white-label.globalMessages.nodata')
      }}</v-alert>
      <div class="chart-container" v-resize:debounce.30="onResize">
        <v-stock-chart
          class="chart"
          :autoResize="false"
          :options="chartConfig"
          :api="chart"
          v-show="!isLoading && !isNoData"
        />
      </div>
      <div class="summary" v-show="!isLoading && !isNoData" ref="summaryEl">
        <header>{{ i18n('summary.title') }}</header>
        <div class="company-name">{{ summaryData.companyName }}</div>
        <div class="price">
          {{ i18n('summary.price') }}: {{ formatCurrency(summaryData.price, summaryData.currency) }}
        </div>
        <div class="highlight">{{ i18n('summary.nextEarnings') }}</div>
        <div v-if="summaryData.nextEarningsDate">
          {{
            `${earningsDate} (${
              summaryData.nextEarningsDate
                ? moment(summaryData.nextEarningsDate).diff(momentTZ().startOf('day'), 'days') +
                  ' ' +
                  i18n('days', 'globalLabels')
                : i18n('today', 'globalLabels')
            })`
          }}
        </div>
        <div class="market-phase" v-if="!isNaN(summaryData.nextEarningsMarketPhase)">
          {{ i18n('summary.marketPhaseMap.' + summaryData.nextEarningsMarketPhase) }}
        </div>
        <hr />
        <div class="highlight">
          {{ i18n('summary.consensusAnalystEstimateEPS') }}<br />{{ i18n('summary.forTheQuarter') }}
        </div>
        <div>{{ formatCurrency(summaryData.quarterEstimate, summaryData.estCurrency) }}</div>
        <div class="highlight">{{ i18n('summary.analystRecommendation.title') }}</div>
        <div
          v-if="summaryData.analystRecommendation"
          :class="['analyst-recommendation', summaryData.analystRecommendation.cssClass]"
        >
          {{ i18n('summary.analystRecommendation.' + summaryData.analystRecommendation.text) }}
        </div>
        <hr />
        <div class="highlight">{{ i18n('summary.impliedVolatility') }}</div>
        <template v-if="summaryData.impliedVolatility">
          <div>
            {{ summaryData.impliedVolatility.curr }}% ({{
              i18n('summary.volatilityNotes.' + summaryData.impliedVolatility.note.toLowerCase())
            }})
          </div>
          <div class="highlight">{{ i18n('summary.impliedVolatility') }} - {{ i18n('summary.52WRange') }}</div>
          <div class="impl-vola-range">
            <span>{{ summaryData.impliedVolatility.low }}%</span>
            <span class="range"
              ><span class="mark" :style="{ left: summaryData.impliedVolatility.markPos + '%' }"></span
            ></span>
            <span>{{ summaryData.impliedVolatility.high }}%</span>
          </div>
        </template>
        <div v-else>{{ i18n('summary.n/a') }}</div>
        <hr />
        <div class="highlight">{{ i18n('summary.probabilityRanges') }}</div>
        <template v-if="summaryData.probabilityRanges">
          <div>
            68% {{ this.i18n('summary.probabilityBetween') }}
            {{
              formatCurrency(Math.max(0, +summaryData.price - summaryData.probabilityRanges['68%']), undefined, true)
            }}
            -
            {{ formatCurrency(+summaryData.price + summaryData.probabilityRanges['68%'], undefined, true) }}
          </div>
          <div>
            95%
            {{ this.i18n('summary.probabilityBetween') }}
            {{
              formatCurrency(Math.max(0, +summaryData.price - summaryData.probabilityRanges['95%']), undefined, true)
            }}
            -
            {{ formatCurrency(+summaryData.price + summaryData.probabilityRanges['95%'], undefined, true) }}
          </div>
        </template>
        <template v-else
          >-</template
        >
      </div>
    </div>
    <v-alert v-else align="center" class="m-2" show variant="light">{{ i18n('nosymbol', 'globalMessages') }}</v-alert>
    <b-toaster :name="notifierId"></b-toaster>
  </div>
</template>

<script>
import { API } from '@state/api';
import { toTimezoneDate } from '@/utils/date';
import CLoader from '@custom/CLoader';
import CStreamingError from '@custom/CStreamingError';
import CWidgetHeader from '@custom/widgetContainer/CWidgetHeader';
import CWidgetControlBar from '@custom/widgetContainer/CWidgetControlBar';
import CWidgetControlBarGroup from '@custom/widgetContainer/CWidgetControlBarGroup';
import CIncrementalSearch from '@custom/CIncrementalSearch';
import VStockChart from '@base/VStockChart';
import widgetCommons from '@mixins/widgetCommons';
import moment from 'moment-timezone';
import resize from 'vue-resize-directive';
import { getSymbolInfo } from '@widgets/WOrderEntryWidget/orderUtils';

let vm;

export default {
  name: 'w-earnings-analyzer-widget',
  mixins: [widgetCommons],
  components: {
    VStockChart,
    CLoader,
    CStreamingError,
    CWidgetHeader,
    CWidgetControlBar,
    CWidgetControlBarGroup,
    CIncrementalSearch
  },
  directives: {
    resize
  },
  props: {
    wid: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      isLoading: false,
      isNoData: false,
      chart: {},
      chartConfig: {
        lang: {
          accessibility: {
            screenReaderSection: {
              beforeRegionLabel: undefined
            },
            chartContainerLabel: this.getChartTitle(),
            svgContainerLabel: this.getChartTitle(),
            defaultChartTitle: this.getChartTitle()
          }
        },
        accessibility: {
          enabled: true,
          keyboardNavigation: {
            enabled: true,
            seriesNavigation: {
              mode: 'normal',
              skipNullPoints: true
            }
          },
          series: { pointDescriptionEnabledThreshold: false },
          point: {
            descriptionFormatter: undefined
          }
        },
        chart: {
          spacing: [10, 0, 4, 10]
        },
        title: {
          text: null
        },
        legend: {
          enabled: true,
          margin: -2
        },
        rangeSelector: {
          enabled: false
        },
        navigator: {
          enabled: false
        },
        scrollbar: {
          enabled: false
        },
        tooltip: {
          dateTimeLabelFormats: {
            day: '%m/%d/%Y',
            week: '%m/%d/%Y'
          },
          footerFormat: '</div>',
          headerFormat: '<div>{point.date}</div><hr><div>',
          pointFormatter() {
            return `
              <div class="point series-${this.series.colorIndex}">
                <div class="icon"></div>
                <div class="label">${this.series.name}</div>
                <div class="value">${
                  !isNaN(this.low)
                    ? vm.$f(this.low, 'float') + ' - ' + vm.$f(this.high, 'float')
                    : vm.$f(this.y, 'float')
                }</div>
              </div>`;
          },
          shape: 'callout',
          shared: true,
          split: false,
          useHTML: true
        },
        xAxis: {
          ordinal: false,
          accessibility: {
            rangeDescription: undefined
          }
        },
        yAxis: [
          {
            labels: {
              align: 'left'
            },
            height: '50%',
            offset: -5,
            top: '0%',
            accessibility: {
              description: undefined
            }
          },
          {
            labels: {
              align: 'left'
            },
            height: '50%',
            offset: -5,
            top: '50%',
            accessibility: {
              description: undefined
            }
          }
        ],
        series: []
      },
      hasStreamingError: false,
      moment,
      priceData: [],
      seriesActual: {
        colorIndex: 1,
        dataGrouping: {
          enabled: false
        },
        events: {
          legendItemClick: ev => {
            this.controlShowActual = !ev.target.visible;
          }
        },
        id: 'actual',
        index: 1,
        marker: {
          enabled: true,
          radius: 3,
          symbol: 'circle'
        },
        get name() {
          return vm.i18n('chart.actualEPS');
        },
        type: 'spline',
        yAxis: 1,
        zIndex: 3
      },
      seriesEstimateEPS: {
        colorIndex: 0,
        dataGrouping: {
          enabled: false
        },
        events: {
          legendItemClick: ev => {
            this.controlShowAnalystsEstimate = !ev.target.visible;
          }
        },
        id: 'estimateEPS',
        index: 0,
        marker: {
          enabled: true,
          radius: 3,
          symbol: 'circle'
        },
        get name() {
          return vm.i18n('chart.analystsEstimateEPS');
        },
        type: 'spline',
        yAxis: 1,
        zIndex: 4
      },
      seriesMinMax: {
        colorIndex: 4,
        dataGrouping: {
          enabled: false
        },
        events: {
          legendItemClick: ev => {
            this.controlShowMinMax = !ev.target.visible;
          }
        },
        id: 'minmax',
        index: 4,
        get name() {
          return vm.i18n('chart.highLowAnalystsEPSRange');
        },
        pointWidth: 25,
        type: 'hlrange',
        yAxis: 1,
        zIndex: 2
      },
      seriesPrice: {
        colorIndex: 2,
        dataGrouping: {
          enabled: false
        },
        id: 'price',
        index: 2,
        get name() {
          return vm.i18n('chart.price');
        },
        showInLegend: false,
        softThreshold: true,
        tooltip: {
          headerFormat: '<div>{point.key}</div><hr><div>',
          pointFormatter() {
            return `
                <div class="point">
                  <div class="label">${vm.i18n('chart.ticker')}</div><div class="value highlight">${vm.ric}</div>
                </div>
                <div class="point">
                  <div class="label">${vm.i18n('chart.price')}</div><div class="value ${
              this.open > this.close ? 'negative' : this.close > this.open ? 'positive' : ''
            }">${vm.$f(this.y, 'float.price')}</div>
                </div>
              `;
          }
        },
        yAxis: 0
      },
      seriesStdDeviation: {
        colorIndex: 3,
        dataGrouping: {
          enabled: false
        },
        events: {
          legendItemClick: ev => {
            this.controlShowStdDeviation = !ev.target.visible;
          }
        },
        id: 'stdDeviation',
        index: 3,
        lineWidth: 0,
        get name() {
          return vm.i18n('chart.standardDeviation');
        },
        type: 'areasplinerange',
        yAxis: 1,
        zIndex: 1
      },
      subscriptionIdOptions: '',
      subscriptionIdPrice: '',
      summaryData: {}
    };
  },
  computed: {
    chartTypeOptions() {
      const path = 'white-label.globalLabels.chart.type.options.';
      return [
        { value: 'line', text: this.$t(path + 'line') },
        { value: 'area', text: this.$t(path + 'area') },
        { value: 'ohlc', text: this.$t(path + 'bar') },
        { value: 'candlestick', text: this.$t(path + 'candlestick') }
      ];
    },
    controlChartType() {
      return this.widgetStore.chartType;
    },
    controlFrequency() {
      return this.widgetStore.frequency;
    },
    controlPeriod() {
      return this.widgetStore.period;
    },
    controlShowActual: {
      get() {
        return this.widgetStore.showSeriesActual;
      },
      set(visible) {
        this.$store.commit(`widget/${this.wid}/SHOW_SERIES_ACTUAL_CHANGED`, visible);
      }
    },
    controlShowAnalystsEstimate: {
      get() {
        return this.widgetStore.showSeriesAnalystsEstimate;
      },
      set(visible) {
        this.$store.commit(`widget/${this.wid}/SHOW_SERIES_ANALYSTS_ESTIMATE_CHANGED`, visible);
      }
    },
    controlShowFutureEstimation: {
      get() {
        return this.widgetStore.showFutureEstimation;
      },
      set(visible) {
        this.$store.commit(`widget/${this.wid}/SHOW_FUTURE_ESTIMATION_CHANGED`, visible);
        this.fetch();
      }
    },
    controlShowMinMax: {
      get() {
        return this.widgetStore.showSeriesMinmax;
      },
      set(visible) {
        this.$store.commit(`widget/${this.wid}/SHOW_SERIES_MINMAX_CHANGED`, visible);
      }
    },
    controlShowStdDeviation: {
      get() {
        return this.widgetStore.showSeriesStdDeviation;
      },
      set(visible) {
        this.$store.commit(`widget/${this.wid}/SHOW_SERIES_STDDEVIATION_CHANGED`, visible);
      }
    },
    earningsDate() {
      moment.locale(this.$i18n.locale);
      return this.summaryData.nextEarningsDate ? moment(this.summaryData.nextEarningsDate).format('MMMM Do, Y') : '';
    },
    dateFrom() {
      return +moment()
        .subtract(this.widgetStore.period, 'years')
        .startOf('day');
    },
    frequencyOptions() {
      return [
        { value: 'W', text: this.i18n('controlbar.frequency.options.weekly') },
        { value: 'M', text: this.i18n('controlbar.frequency.options.monthly') }
      ];
    },
    periodOptions() {
      const path = 'white-label.globalLabels.chart.period.options.';
      return [
        { value: 1, text: this.$t(path + '1y') },
        { value: 2, text: this.$t(path + '2y') },
        { value: 3, text: this.$t(path + '3y') },
        { value: 4, text: this.$t(path + '4y') },
        { value: 5, text: this.$t(path + '5y') }
      ];
    },
    ric() {
      return this.widgetStore.ric;
    }
  },
  watch: {
    dateFrom: {
      handler(val) {
        if (this.chart.update) {
          this.chart.update({
            xAxis: {
              min: val
            }
          });
        } else this.chartConfig.xAxis.min = val;
      },
      immediate: true
    },
    fontSize() {
      setTimeout(() => {
        // we need to reflow chart elements after font changed, but reflow doesn't happen, if the size of the chart container doesn't change. Let's kinda change it then
        this.chart.chart.container.parentNode.style.width =
          this.chart.chart.container.parentNode.offsetWidth - 2 + 'px';
        this.onResize();
        this.chart.chart.container.parentNode.style.width = '';
      }, 1500);
    },
    ric() {
      getSymbolInfo(this.ric).then(({ ticker, ticketType, underlyingRic }) => {
        switch (ticketType) {
          case 'option': {
            const ulRic = Array.isArray(underlyingRic) ? underlyingRic[0] : underlyingRic;
            if (!ulRic) this.isNoData = true;
            else {
              this.$notify.info('', `${this.i18n('notification.usingUnderlying')} <b>${ticker}</b>`, {
                group: this.notifierId,
                closeButton: true,
                duration: 4000
              });
              if (this.ric !== ulRic) {
                this.onUpdateSymbol(ulRic); //let the next tick handle it
              }
            }
            break;
          }
          case 'stock': {
            this.fetch();
            break;
          }
          default: {
            this.isNoData = true;
          }
        }
      });
    },
    '$store.state.applicationSettings.general.settings.applicationLanguage'() {
      this.chartConfig.lang.accessibility.chartContainerLabel = this.getChartTitle();
      this.chartConfig.lang.accessibility.svgContainerLabel = this.getChartTitle();
      this.chartConfig.lang.accessibility.defaultChartTitle = this.getChartTitle();
    }
  },
  methods: {
    onResize() {
      const r = this.$el.querySelector('.chart-container').getBoundingClientRect();
      this.chart.setSize(r.width, r.height);
    },
    // controls update
    onUpdateChartType(chartType) {
      this.$store.commit(`widget/${this.wid}/CHART_TYPE_CHANGED`, chartType);
      this.updatePrices(true);
    },
    onUpdateFrequency(frequency) {
      this.$store.commit(`widget/${this.wid}/FREQUENCY_CHANGED`, frequency);
      this.fetch();
    },
    onUpdatePeriod(period) {
      this.$store.commit(`widget/${this.wid}/PERIOD_CHANGED`, period);
      this.fetch();
    },
    onUpdateSymbol(symbol) {
      this.$store.commit(`widget/${this.wid}/RIC_CHANGED`, String(symbol));
    },
    // Load Data
    fetchHistoricalPrices() {
      const fids = ['h._DATE_END', 'h.OPEN_PRC', 'h.CLOSE_PRC', 'h.HIGH_1', 'h.LOW_1'];
      return API.Rest.get(
        `/timeseries/historical` +
          `?ric=${this.ric}` +
          `&fids=${fids.join(',')}` +
          `&fromDate=${moment(this.dateFrom)
            .subtract(1, this.controlFrequency)
            .format('YYYY-MM-DD')}` +
          `&toDate=${moment().format('YYYY-MM-DD')}` +
          `&samples=${this.controlFrequency}`
      ).then(response => {
        if (response.data.substatus === 'Stale') {
          return new Promise(resolve => window.setTimeout(resolve, 3000)).then(this.fetchHistoricalPrices);
        }
        this.priceData = response.data.data;
        if (this.priceData.length === 0) {
          this.isNoData = true;
        }
      });
    },
    fetchEstimatedEarnings() {
      const fids = [
        'earn.ACTUAL',
        'earn.COMBI_DATE',
        'earn.EST_MEAN',
        'earn.EST_HIGH',
        'earn.EST_LOW',
        'earn.EST_STD_DEV'
      ];
      const dateTo = moment();
      if (this.controlShowFutureEstimation) {
        dateTo.add(2, 'year');
      }

      return API.Rest.get(
        `/company/earnings` +
          `?rics=${this.ric}` +
          `&fids=${fids.join()}` +
          `&fromDate=${moment(this.dateFrom)
            .subtract(1, 'quarter')
            .format('YYYY-MM-DD')}` +
          `&toDate=${dateTo.format('YYYY-MM-DD')}` +
          `&sortFids=COMBI_DATE` +
          `&dateFilterFid=COMBI_DATE` +
          `&periodTypes=Q` +
          `&sortDirs=A` +
          `&sortTypes=A` +
          `&pageSize=1000`
      ).then(response => response.data);
    },
    fetchSummaryData() {
      return Promise.all([
        API.Rest.get(
          `/quote/info?rics=${this.ric}&fids=q._DSPLY_NAME,q._CURRENCY,q._TRDPRC_1,q.IMP_VOLT,rkd.RECOM_MEAN_CURR`
        ),
        API.Rest.get(
          `/company/earnings?rics=${
            this.ric
          }&fids=earn.COMBI_DATE,earn.MARKET_PHASE,earn.EST_MEAN,earn.EST_CURRENCY&fromDate=${moment().format(
            'YYYY-MM-DD'
          )}&sortFids=COMBI_DATE&sortDirs=A&pageSize=1`
        ),
        API.Rest.get(`/product/mymarkets/volarange?ric=${this.ric}`)
          .then(response => {
            const propResponse = {
              data: {
                data: [],
                metadata: {
                  'calc.CURR_IMPL_VOLA': { type: 'float' },
                  'calc.IMPL_VOLA_HIGH': { type: 'float' },
                  'calc.IMPL_VOLA_LOW': { type: 'float' }
                }
              }
            };
            const data = response.data.data;
            if (!data || !data.length) return propResponse;
            propResponse.data.data[0] = {
              'calc.CURR_IMPL_VOLA': data[0].Current,
              'calc.IMPL_VOLA_HIGH': data[0].Max,
              'calc.IMPL_VOLA_LOW': data[0].Min
            };
            return propResponse;
          })
          .catch(e => console.error(e))
      ]).then(responses => {
        const dataTypes = {};
        const data = {};
        responses.forEach(response => {
          if (response) {
            Object.keys(response.data.metadata).forEach(fid => {
              dataTypes[fid] = response.data.metadata[fid].type;
            });
            Object.assign(data, response.data.data[0]);
          }
        });
        return { dataTypes, data };
      });
    },
    fetch() {
      if (!this.ric) return;
      this.isLoading = true;
      this.isNoData = false;
      return Promise.all([this.fetchHistoricalPrices(), this.fetchEstimatedEarnings(), this.fetchSummaryData()])
        .then(results => {
          this.updatePrices();
          this.updateEstimate(results[1].data);
          this.onResize();
          this.updateSummary(results[2]);
          this.getProbabilityRanges(results[2].data['q._TRDPRC_1']);

          if (this.chart.xAxis) {
            this.chartConfig.xAxis.accessibility.rangeDescription = `${this.$t('white-label.globalLabels.range')}
              ${moment(new Date(this.chart.xAxis[0].min)).format('dddd MMMM Do YYYY')}
              ${this.$t('white-label.globalLabels.to')}
              ${moment(new Date(this.chart.xAxis[0].max)).format('dddd MMMM Do YYYY')}`;
          }
          this.chartConfig.yAxis[0].accessibility.description = this.i18n('chart.price');
          this.chartConfig.yAxis[1].accessibility.description = `${this.i18n('chart.standardDeviation')},${this.i18n(
            'chart.actualEPS'
          )},${this.i18n('chart.analystsEstimateEPS')},${this.i18n('chart.highLowAnalystsEPSRange')}`;
        })
        .catch(err => {
          console.error(err);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    formatCurrency(val, currency = this.summaryData.currency, price2decimals = false) {
      if (price2decimals) {
        val = Math.round(val * 100) / 100;
      }
      return `${currency === 'USD' ? '$' : ''}${this.$f(val, 'float.price')}${
        currency === 'USD' ? '' : currency === 'EUR' ? '€' : ' ' + currency
      }`;
    },
    getProbabilityRanges(price) {
      API.Rest.get(`/product/mymarkets/atmoptions?ric=${this.ric}&count=1`).then(res => {
        for (const optBlock of res.data.data) {
          if (
            +price >= +optBlock.lowerBorder &&
            (!optBlock.higherBorder || +price <= +optBlock.higherBorder) &&
            (this.summaryData.probabilityRanges.optBlock.call !== optBlock.call ||
              this.summaryData.probabilityRanges.optBlock.put !== optBlock.put)
          ) {
            this.streamingUnsubscribe();
            this.summaryData.probabilityRanges.optBlock = optBlock;
            this.streamingSubscribe();
            break;
          }
        }
      });
    },
    // Chart
    getUTCTimestamp(date) {
      return Date.parse(new Date(date).toUTCString());
    },
    getFormatedDate(date) {
      const lang = this.$store.state.applicationSettings.general.settings.applicationLanguage.substr(0, 2);
      moment.locale(lang);
      if (lang === 'fr') {
        moment.updateLocale('fr', {
          monthsShort: ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jui', 'Jui', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'],
          weekdaysShort: ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam']
        });
      } else {
        // reset the locale because, upadting 'fr' locale also effects global locale causing issue in en
        moment.updateLocale('fr', null);
      }
      const localDate = moment.tz(date, 'America/Toronto').format('ddd, MMM D YYYY');
      return localDate;
    },
    getCandleWidth() {
      if (this.controlFrequency === 'M' && this.controlPeriod > 0) {
        return 24 - (this.controlPeriod - 1) * 4;
      } else {
        // let highcharts decide
        return undefined;
      }
    },
    handleStreamingResponseOptions(res, error) {
      if (error) {
        this.hasStreamingError = true;
        return console.warn('Option streaming error', error);
      }
      this.hasStreamingError = false;
      const savedData = this.summaryData.probabilityRanges.data;
      for (const payload of res.payload) {
        // console.warn(payload.ric, payload.data);
        const optType = payload.ric === this.summaryData.probabilityRanges.optBlock.call ? 'call' : 'put';
        Object.entries(payload.data).forEach(
          ([fid, val]) => (savedData[optType][fid === 'q._BID' ? 'bid' : 'ask'] = +val)
        );
        if (savedData.call.bid && savedData.call.ask && savedData.put.bid && savedData.put.ask) {
          const atmStraddle = (savedData.call.bid + savedData.call.ask + savedData.put.bid + savedData.put.ask) / 2;
          // console.warn(savedData.call.bid, savedData.call.ask, savedData.put.bid, savedData.put.ask, atmStraddle);
          this.summaryData.probabilityRanges['68%'] = atmStraddle * 1.25;
          this.summaryData.probabilityRanges['95%'] = atmStraddle * 2.5;
        }
      }
      // reflow chart on summary container width change, but only if the width fluctuates by > 5px
      const prevWidth = this.$refs.summaryEl.offsetWidth;
      setTimeout(() => {
        if (Math.abs(prevWidth - this.$refs.summaryEl.offsetWidth) > 5) {
          this.onResize();
        }
      }, 0);
    },
    handleStreamingResponsePrice(res, error) {
      if (error) {
        this.hasStreamingError = true;
        return console.warn('Price streaming error', error);
      }
      this.hasStreamingError = false;
      if (res.payload[0].ric !== this.ric || !res.payload[0].data['q._TRDPRC_1']) return;
      this.summaryData.price = res.payload[0].data['q._TRDPRC_1'];
      const optBlock = this.summaryData.probabilityRanges.optBlock;
      // console.warn(this.summaryData.price, optBlock.lowerBorder, optBlock.higherBorder);
      if (+this.summaryData.price < +optBlock.lowerBorder || +this.summaryData.price > +optBlock.higherBorder) {
        this.getProbabilityRanges(this.summaryData.price);
      }
    },
    momentTZ(date) {
      return moment(toTimezoneDate(this.$store.state.applicationSettings.general.settings.timezone, date));
    },
    streamingConnect(args) {
      return new Promise((resolve, reject) => {
        if (API.Streaming.isConnected) resolve(args);
        else
          API.Streaming.connect({
            timeout: 10000,
            monitor: {
              interval: 10000,
              timeout: 5000
            },
            visibilityMonitorEnabled: true
          }).then(() => resolve(args), reject);
      });
    },
    streamingSubscribe() {
      this.streamingConnect().then(() => {
        this.subscriptionIdPrice = API.Streaming.Quote.subscribe(
          {
            rics: [this.ric],
            fids: ['q._TRDPRC_1']
          },
          // Subscribe Callback
          (_res, error) => {
            if (error) {
              console.error('Streaming price subscription', error);
              this.hasStreamingError = true;
            } else this.hasStreamingError = false;
          },
          // Snapshot Callback
          this.handleStreamingResponsePrice,
          // Update Callback
          this.handleStreamingResponsePrice
        );
        this.subscriptionIdOptions = API.Streaming.Quote.subscribe(
          {
            rics: [this.summaryData.probabilityRanges.optBlock.call, this.summaryData.probabilityRanges.optBlock.put],
            fids: ['q._BID', 'q._ASK']
          },
          // Subscribe Callback
          (_res, error) => {
            if (error) {
              console.error('Streaming options subscription', error);
              this.hasStreamingError = true;
            } else this.hasStreamingError = false;
          },
          // Snapshot Callback
          this.handleStreamingResponseOptions,
          // Update Callback
          this.handleStreamingResponseOptions
        );
      });
    },
    streamingUnsubscribe() {
      if (this.subscriptionIdPrice)
        this.streamingConnect().then(() => API.Streaming.Quote.unsubscribe(this.subscriptionIdPrice));
      if (this.subscriptionIdOptions)
        this.streamingConnect().then(() => API.Streaming.Quote.unsubscribe(this.subscriptionIdOptions));
    },
    // find and mark the lowest grid line on the price y-axis, it will act as a divider between y-axes; this line is now always first/last, so not findable via css selector
    updateAxesDivider() {
      this.$nextTick(() => {
        const priceAxisGrid = this.chart.chart.yAxis[0].gridGroup && this.chart.chart.yAxis[0].gridGroup.element;
        if (!priceAxisGrid) return;
        const lowestLine = Array.from(priceAxisGrid.children).reduce((lowestLine, line) => {
          if (lowestLine.getPointAtLength(0).y < line.getPointAtLength(0).y) lowestLine = line;
          return lowestLine;
        });
        const prevLowestLine = priceAxisGrid.querySelector('.mm5-lowest-line');
        if (prevLowestLine) {
          if (prevLowestLine === lowestLine) return;
          prevLowestLine.classList.remove('mm5-lowest-line');
        }
        lowestLine.classList.add('mm5-lowest-line');
      });
    },
    updatePrices(redrawChart = false) {
      const isOHLC = ['candlestick', 'ohlc'].includes(this.controlChartType);
      this.chart.get(this.seriesPrice.id).update(
        {
          type: this.controlChartType,
          data: this.priceData.map(item => {
            const point = {
              x: this.getUTCTimestamp(item['h._DATE_END']),
              y: parseFloat(item['h.CLOSE_PRC']),
              // include open/close in all types of charts for tooltip price change coloring
              open: parseFloat(item['h.OPEN_PRC']),
              close: parseFloat(item['h.CLOSE_PRC'])
            };
            if (isOHLC) {
              point.high = parseFloat(item['h.HIGH_1']);
              point.low = parseFloat(item['h.LOW_1']);
            }
            return point;
          }),
          pointWidth: isOHLC ? this.getCandleWidth() : undefined,
          softThreshold: isOHLC
        },
        redrawChart
      );
      this.updateAxesDivider();
    },
    updateEstimate(estimate) {
      this.chart.get(this.seriesActual.id).setData(
        estimate.map(item => {
          return {
            x: this.getUTCTimestamp(item['earn.COMBI_DATE']),
            y: parseFloat(item['earn.ACTUAL']),
            date: this.getFormatedDate(item['earn.COMBI_DATE'])
          };
        }),
        false
      );
      this.chart.get(this.seriesEstimateEPS.id).setData(
        estimate.map(item => {
          return {
            x: this.getUTCTimestamp(item['earn.COMBI_DATE']),
            y: parseFloat(item['earn.EST_MEAN']),
            date: this.getFormatedDate(item['earn.COMBI_DATE'])
          };
        }),
        false
      );
      this.chart.get(this.seriesStdDeviation.id).setData(
        estimate.map(item => {
          return {
            x: this.getUTCTimestamp(item['earn.COMBI_DATE']),
            high: parseFloat(item['earn.EST_MEAN']) + parseFloat(item['earn.EST_STD_DEV']),
            low: parseFloat(item['earn.EST_MEAN']) - parseFloat(item['earn.EST_STD_DEV']),
            date: this.getFormatedDate(item['earn.COMBI_DATE'])
          };
        }),
        false
      );
      const hlSeries = this.chart.get(this.seriesMinMax.id);
      // hlSeries throws errors after being updated in hidden state, so let's show it for a moment
      const setVisibleForUpdate = !hlSeries.visible;
      if (setVisibleForUpdate) {
        hlSeries.setVisible(true, false);
      }
      hlSeries.setData(
        estimate.map(item => {
          return {
            x: this.getUTCTimestamp(item['earn.COMBI_DATE']),
            low: parseFloat(item['earn.EST_LOW']),
            high: parseFloat(item['earn.EST_HIGH'])
          };
        }),
        false
      );
      if (setVisibleForUpdate) {
        hlSeries.setVisible(false, false);
      }
    },
    updateSummary({ data }) {
      if (!Object.keys(data).length) throw new Error('Summary data is missing');
      const analystRecommendation = {
        note: +data['rkd.RECOM_MEAN_CURR']
      };
      if (analystRecommendation.note < 1.5) {
        analystRecommendation.text = 'strongBuy';
        analystRecommendation.cssClass = 'strong-buy';
      } else if (analystRecommendation.note >= 1.5 && analystRecommendation.note < 2.5) {
        analystRecommendation.text = 'buy';
        analystRecommendation.cssClass = 'buy';
      } else if (analystRecommendation.note >= 2.5 && analystRecommendation.note < 3.5) {
        analystRecommendation.text = 'hold';
        analystRecommendation.cssClass = 'hold';
      } else if (analystRecommendation.note >= 3.5 && analystRecommendation.note < 4.5) {
        analystRecommendation.text = 'sell';
        analystRecommendation.cssClass = 'sell';
      } else {
        analystRecommendation.text = 'strongSell';
        analystRecommendation.cssClass = 'strong-sell';
      }
      let impliedVolatility;
      if ('calc.CURR_IMPL_VOLA' in data && 'calc.IMPL_VOLA_LOW' in data && 'calc.IMPL_VOLA_HIGH' in data) {
        impliedVolatility = {
          curr: Math.round(data['calc.CURR_IMPL_VOLA']),
          low: Math.round(data['calc.IMPL_VOLA_LOW']),
          high: Math.round(data['calc.IMPL_VOLA_HIGH'])
        };
        impliedVolatility.markPos =
          ((impliedVolatility.curr - impliedVolatility.low) / (impliedVolatility.high - impliedVolatility.low)) * 100;
        impliedVolatility.note =
          impliedVolatility.markPos < 33.3 ? 'Low' : impliedVolatility.markPos > 66.6 ? 'High' : 'Moderate';
      }

      this.summaryData = {
        companyName: data['q._DSPLY_NAME'],
        price: data['q._TRDPRC_1'],
        currency: data['q._CURRENCY'],
        estCurrency: data['earn.EST_CURRENCY'],
        nextEarningsDate: data['earn.COMBI_DATE'],
        quarterEstimate: data['earn.EST_MEAN'],
        nextEarningsMarketPhase: data['earn.MARKET_PHASE'],
        analystRecommendation,
        impliedVolatility,
        probabilityRanges: {
          '68%': 0,
          '95%': 0,
          data: { call: { bid: 0, ask: 0 }, put: { bid: 0, ask: 0 } },
          optBlock: { call: '', put: '' }
        }
      };
    },
    descriptionFormatter(point) {
      const output = [];
      let calculatedValue = '';
      output.push(point.index);
      output.push(moment(new Date(point.x)).format('dddd MMMM Do YYYY, h:mm'));
      output.push(vm.i18n('chart.ticker'));
      output.push(this.ric);
      output.push(point.series.name);

      if (!isNaN(point.low)) {
        if (point.series.userOptions.id === 'price') {
          calculatedValue = Math.round(point.y * 100) / 100;
        } else {
          calculatedValue = Math.round(point.low * 100) / 100 + ' - ' + Math.round(point.high * 100) / 100;
        }
      } else {
        calculatedValue = Math.round(point.y * 100) / 100;
      }

      if (this._.isNaN(calculatedValue)) calculatedValue = this.$t('white-label.globalLabels.noValue');
      output.push(calculatedValue);

      return output.join(' ');
    },
    getChartTitle() {
      return this.wid
        ? `${this.$t(`white-label.widgets.earningsAnalyzer.widget-title`)} ${this.$t(
            'white-label.globalLabels.chartGraphImage'
          )}`
        : '';
    }
  },
  created() {
    vm = this;
    this.seriesEstimateEPS.visible = this.controlShowAnalystsEstimate;
    this.seriesActual.visible = this.controlShowActual;
    this.seriesStdDeviation.visible = this.controlShowStdDeviation;
    this.seriesMinMax.visible = this.controlShowMinMax;
    this.chartConfig.series = [
      this.seriesPrice,
      this.seriesEstimateEPS,
      this.seriesActual,
      this.seriesStdDeviation,
      this.seriesMinMax
    ];
  },
  mounted() {
    this.fetch();
    this.chartConfig.accessibility.point.descriptionFormatter = this.descriptionFormatter;
  },
  beforeDestroy() {
    this.streamingUnsubscribe();
  }
};
</script>

<style lang="scss">
@import '~@/styles/widgets/earnings-analyzer-widget';
</style>
